import {createStyles, Textarea} from '@mantine/core';
import React, {useState} from 'react';
import _ from 'underscore';

import {MAX_ANALYSIS_DESCRIPTION_LENGTH} from 'analysis-tool/AnalysisToolConstants';
import * as Analytics from 'analytics/Analytics';
import {AnalyticsMetadataForContext} from 'analytics/AnalyticsTypes';
import {ActionOrigin, ANALYTICS_MODULE, COMMON_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import FolderSelector from 'file-browser/FolderSelector';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import clampEntryName from 'notebook/entry-templates/clampEntryName';
import useStepTypeInfos, {StepTypeInfo} from 'pipelines/hooks/useStepTypeInfosQuery';
import getTestProps from 'test/lib/cypress/getTestProps';
import {InlineBannerProps, InlineBannerType} from 'ui-library/banners/InlineBanner';
import InlineBannerStack from 'ui-library/banners/InlineBannerStack';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import Row from 'ui-library/bs/Row';
import {ButtonStyle} from 'ui-library/buttons/Button';
import useOnMount from 'ui-library/hooks/useOnMount';
import TextInput from 'ui-library/inputs/TextInput';
import Modal from 'ui-library/modals/Modal';
import {withLoadingScreen} from 'util/Promises';

import {cloneAnalysis} from './AnalysisActions';
import {AnalysisGraphQL, isStepTypeUnsupportedByClone} from './AnalysisTypes';

/** @package */
export interface CloneAnalysisModalProps {
  analysisToClone: AnalysisGraphQL;
  onCloneAnalysis: typeof cloneAnalysis;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}

const ANALYTICS_METADATA: AnalyticsMetadataForContext = {
  actionOrigin: ActionOrigin.ANALYSIS_TOOL,
  modalType: 'clone_analysis',
};

const useStyles = createStyles(() => ({
  input: {
    resize: 'vertical',
  },
}));

/** @package */
export const CLONE_ANALYSIS_OUTPUTS_WARNING =
  'This analysis contains steps that are not supported for duplication and will be excluded.';

function getCloneAnalysisWarnings(
  analysisToClone: AnalysisGraphQL,
  stepTypeInfos: ReadonlyMap<PipelineStepType, StepTypeInfo>
): Array<InlineBannerProps> {
  return _.compact([
    analysisToClone.execution.steps.some((step) =>
      isStepTypeUnsupportedByClone(step.definition.stepType, stepTypeInfos)
    )
      ? {
          bannerType: InlineBannerType.WARNING,
          text: CLONE_ANALYSIS_OUTPUTS_WARNING,
        }
      : null,
  ]);
}

/** @package */
export function CloneAnalysisModal({
  analysisToClone,
  onCloneAnalysis,
  onRequestClose,
  onRequestSubmit,
}: CloneAnalysisModalProps): JSX.Element {
  const [name, setName] = useState(clampEntryName(`Copy of ${analysisToClone.name}`));
  const [description, setDescription] = useState(analysisToClone.description);
  const [folderId, setFolderId] = useState<string | null>(analysisToClone.folder.id);

  const {classes} = useStyles();

  const trimmedName = name.trim();
  const isNameBlank = trimmedName === '';

  const normalizedDescription = description?.trim() || null;
  const descriptionLength = normalizedDescription?.length ?? 0;
  const isDescriptionTooLong = descriptionLength > MAX_ANALYSIS_DESCRIPTION_LENGTH;

  const stepTypeInfos = useStepTypeInfos();
  // The query returns every step type, so an empty map means it hasn't resolved yet. Gate submit on it
  // so the "unsupported steps" warning is computed deterministically before a user can duplicate.
  const stepTypeInfosLoaded = stepTypeInfos.size > 0;
  const warnings = getCloneAnalysisWarnings(analysisToClone, stepTypeInfos);

  async function handleRequestSubmit(): Promise<void> {
    await withLoadingScreen(
      onCloneAnalysis(analysisToClone.id, {
        folderId: folderId!,
        name: trimmedName,
        description: normalizedDescription,
      }),
      'Duplicating analysis...'
    );
    onRequestSubmit();
  }

  useOnMount(() => {
    Analytics.trackEventWithModule(
      COMMON_ANALYTICS_ACTIONS.OPENED_MODAL,
      ANALYTICS_MODULE.ANALYSIS_TOOL,
      ANALYTICS_METADATA
    );
  });

  return (
    <Modal
      title="Duplicate analysis"
      onRequestSubmit={() => {
        Analytics.trackEventWithModule(
          COMMON_ANALYTICS_ACTIONS.SUBMITTED_MODAL,
          ANALYTICS_MODULE.ANALYSIS_TOOL,
          ANALYTICS_METADATA
        );
        handleRequestSubmit();
      }}
      onRequestClose={() => {
        Analytics.trackEventWithModule(
          COMMON_ANALYTICS_ACTIONS.CLOSED_MODAL,
          ANALYTICS_MODULE.ANALYSIS_TOOL,
          ANALYTICS_METADATA
        );
        onRequestClose();
      }}
      buttons={[
        {
          submit: true,
          text: 'Duplicate',
          style: ButtonStyle.SECONDARY,
          disabled: !stepTypeInfosLoaded || isNameBlank || isDescriptionTooLong,
          tooltipTitle: !stepTypeInfosLoaded
            ? 'Steps are not loaded yet. Please try again later.'
            : isNameBlank
              ? 'Analysis name must be non-empty.'
              : isDescriptionTooLong
                ? `Description must be at most ${MAX_ANALYSIS_DESCRIPTION_LENGTH} characters.`
                : null,
        },
      ]}
      dataTestAttrs={{component: 'CloneAnalysisModal', element: 'root'}}
    >
      <FormGroup>
        <Row>
          <Col md={6}>
            <Label>Name</Label>
            <TextInput
              value={name}
              hasError={isNameBlank}
              onChange={(e) => {
                setName(clampEntryName(e.target.value));
              }}
              autoFocus={true}
              {...getTestProps({component: 'CloneAnalysisModal', element: 'nameInput'})}
            />
          </Col>
          <Col md={6}>
            <Label>Folder</Label>
            <FolderSelector
              enableRedesign={true}
              showNotebook={true}
              value={folderId}
              onChange={(e: {target: {value: string}}) => {
                setFolderId(e.target.value);
              }}
              unsetOnFetchFail={true}
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <Textarea
              // Mantine's Textarea sets `resize: none`; the legacy `.modal-body textarea` rule currently
              // overrides it, so we set it explicitly to keep vertical resizing regardless of the modal wrapper.
              classNames={{input: classes.input}}
              inputWrapperOrder={['label', 'input', 'description', 'error']}
              label="Description"
              description={`${descriptionLength} of ${MAX_ANALYSIS_DESCRIPTION_LENGTH} allowed characters.`}
              error={isDescriptionTooLong}
              value={description ?? ''}
              onChange={(e) => {
                setDescription(e.currentTarget.value);
              }}
            />
          </Col>
        </Row>
      </FormGroup>
      {warnings.length > 0 && <InlineBannerStack messages={warnings} />}
    </Modal>
  );
}

export default function ConnectedCloneAnalysisModal(
  props: Omit<CloneAnalysisModalProps, 'onCloneAnalysis'>
): JSX.Element {
  return <CloneAnalysisModal {...props} onCloneAnalysis={cloneAnalysis} />;
}
